import React, { useState, useEffect, useRef } from "react";
import { ref, set, onValue, get } from 'firebase/database';
import { database, firestore } from '../../firebaseConfig';
import { doc, updateDoc, writeBatch, getDoc } from 'firebase/firestore';
import { addFooterToEmail, convertToHtml } from '../EmailFooterGenerator';
import { 
  generateEmailForContact, 
  updateContactStatus,
  saveLastSenderToDatabase 
} from './BulkModeComponents/bulkModeHelpers';
import { CLOUD_FUNCTION_URLS } from '../../utils/cloudFunctions';

// CSS für den AutoSendModeEmail
import "./AutoSendModeEmail.css";

const AutoSendModeEmail = ({ 
  onClose, 
  userId, 
  userSettings,
  aiSettings,
  userTopics,
  isTopicsLoaded,
  senderEmails,
  topicTypeMapping,
  bulkContacts,
  allData,
  onEmailResult,
  onProcessingChange
}) => {
  // Status-States
  const [isProcessing, setIsProcessing] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [processComplete, setProcessComplete] = useState(false);
  const [progress, setProgress] = useState(0);
  
  // Neue States für mehrstufigen Prozess
  const [currentStep, setCurrentStep] = useState('confirmation'); // 'confirmation', 'cleaning', 'cleaningResults', 'sending', 'complete'
  const [showCleaningResults, setShowCleaningResults] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'kept', 'renamed', 'removed'
  
  // Einstellungs-States
  const [selectedStyle, setSelectedStyle] = useState("Professional & Supportive");
  const [selectedSenderEmail, setSelectedSenderEmail] = useState("");
  const [useRandomSender, setUseRandomSender] = useState(true);
  const [addCleaning, setAddCleaning] = useState(false);
  const [autoCleaningMode, setAutoCleaningMode] = useState(false);
  
  // Cleaning-States
  const [isCleaningStep, setIsCleaningStep] = useState(false);
  const [cleaningProgress, setCleaningProgress] = useState(0);
  const [cleanedContacts, setCleanedContacts] = useState([]);
  const [cleaningResults, setCleaningResults] = useState(null);
  const [removedContacts, setRemovedContacts] = useState([]);
  
  // Ergebnis-States
  const [results, setResults] = useState({
    success: 0,
    failed: 0,
    skipped: 0,
    errors: [],
    allContactsRemoved: false
  });
  const [sentEmails, setSentEmails] = useState([]);
  const [showCleaningDetails, setShowCleaningDetails] = useState(false);
  const [topicSummary, setTopicSummary] = useState({});

  // Benachrichtige die übergeordnete Komponente über Änderungen im Verarbeitungsstatus
  useEffect(() => {
    if (onProcessingChange) {
      onProcessingChange(isProcessing || isCleaningStep);
    }
  }, [isProcessing, isCleaningStep, onProcessingChange]);

  // Verhindere Scrollen im Hintergrund, wenn das Modal geöffnet ist
  useEffect(() => {
    // Füge CSS-Klasse zum Body hinzu, um Scrollen zu verhindern
    document.body.classList.add('auto-send-modal-open');
    
    // Entferne die Klasse beim Unmount
    return () => {
      document.body.classList.remove('auto-send-modal-open');
    };
  }, []);

  // Initialisiere die Absender-E-Mail beim Laden
  useEffect(() => {
    // Entferne die automatische Selektion der ersten E-Mail
    // da wir standardmäßig Random Rotation verwenden wollen
  }, [senderEmails, selectedSenderEmail, useRandomSender]);

  // Lade Topic Summary beim ersten Laden
  useEffect(() => {
    if (bulkContacts && allData && bulkContacts.length > 0 && allData.length > 0) {
      const loadTopicSummary = async () => {
        try {
          const summary = await generateTopicSummary();
          setTopicSummary(summary);
        } catch (error) {
          console.error('Error loading topic summary:', error);
          setTopicSummary({});
        }
      };
      
      loadTopicSummary();
    }
  }, [bulkContacts, allData, userId]);

  // Bestimmt das passende Topic anhand des Kontakt-Status oder AI Tags
  const determineTopicForContact = async (contact) => {
    if (!contact) return "Introduce Yourself";
    
    console.log('📧 [AUTO-SEND] determineTopicForContact for:', contact.business_name || 'Unnamed', contact.id);
    
    // 1. Prüfe zuerst, ob der Kontakt AI Tags hat (höchste Priorität)
    if (contact.tags && typeof contact.tags === 'object') {
      console.log('📧 [AUTO-SEND] AI Tags found in contact:', contact.tags);
      
      // Konvertiere Tags-Objekt zu Array und sortiere nach appliedAt (neueste zuerst)
      const tagsArray = Object.entries(contact.tags).map(([tagId, tagData]) => ({
        id: tagId,
        ...tagData
      }));
      
      // Sortiere nach appliedAt Datum (neueste zuerst)
      tagsArray.sort((a, b) => {
        const dateA = new Date(a.appliedAt || 0);
        const dateB = new Date(b.appliedAt || 0);
        console.log('📧 [AUTO-SEND] 🔍 SORTING DEBUG:');
        console.log('📧 [AUTO-SEND]   Tag A:', { id: a.id, appliedAt: a.appliedAt, parsedDate: dateA.toISOString(), timestamp: dateA.getTime() });
        console.log('📧 [AUTO-SEND]   Tag B:', { id: b.id, appliedAt: b.appliedAt, parsedDate: dateB.toISOString(), timestamp: dateB.getTime() });
        console.log('📧 [AUTO-SEND]   Sort result (dateB - dateA):', dateB.getTime() - dateA.getTime());
        return dateB - dateA; // Neueste zuerst
      });
      
      console.log('📧 [AUTO-SEND] Tags array sorted by date:', tagsArray);
      console.log('📧 [AUTO-SEND] 🎯 SELECTED TAG (index 0):', {
        id: tagsArray[0]?.id,
        appliedAt: tagsArray[0]?.appliedAt,
        appliedTopic: tagsArray[0]?.appliedTopic,
        selectedTopic: tagsArray[0]?.selectedTopic,
        isNewest: true
      });
      
      if (tagsArray.length > 0) {
        const newestTag = tagsArray[0];
        console.log('📧 [AUTO-SEND] Newest tag:', newestTag);
        
        // 🔥 NEUE LOGIK: Lade die AI Tag Settings direkt aus der Datenbank (wie in BulkModeEmail.js)
        if (newestTag.id) {
          console.log('📧 [AUTO-SEND] Searching for AI Tag with ID:', newestTag.id);
          
          try {
            // Lade die AI Tag-Einstellungen
            const aiTagRef = ref(database, `/World/${userId}/settings/aiTags/${newestTag.id}`);
            const aiTagSnapshot = await new Promise((resolve) => {
              onValue(aiTagRef, resolve, { onlyOnce: true });
            });
            
            if (aiTagSnapshot.exists()) {
              const aiTagData = aiTagSnapshot.val();
              console.log('📧 [AUTO-SEND] ✅ Loaded AI Tag settings from database:', {
                selectedTopic: aiTagData.selectedTopic,
                tag: aiTagData.tag,
                name: aiTagData.name
              });
              
              if (aiTagData.selectedTopic) {
                console.log('📧 [AUTO-SEND] ✅ Found selectedTopic from AI Tag settings (like Normal-Mode):', aiTagData.selectedTopic);
                console.log('📧 [AUTO-SEND] 📝 Note: Using AI Tag selectedTopic from settings, not from contact tag');
                return aiTagData.selectedTopic;
              }
            } else {
              console.log('📧 [AUTO-SEND] ⚠️ AI Tag settings not found for ID:', newestTag.id);
            }
          } catch (error) {
            console.error('📧 [AUTO-SEND] Error loading AI Tag settings:', error);
          }
          
          // 🔥 FALLBACK: Prüfe, ob der AI Tag ein selectedTopic hat (falls es doch gespeichert wurde)
          if (newestTag.selectedTopic) {
            console.log('📧 [AUTO-SEND] ✅ Found selectedTopic directly from stored tag:', newestTag.selectedTopic);
            return newestTag.selectedTopic;
          }
        }
        
        // Fallback: Prüfe, ob der gespeicherte Tag ein selectedTopic hat (alte Kopie)
        if (newestTag.selectedTopic) {
          console.log('📧 [AUTO-SEND] ✅ Using selectedTopic from stored tag (legacy):', newestTag.selectedTopic);
          return newestTag.selectedTopic;
        }
      } else {
        console.log('📧 [AUTO-SEND] No AI tags found in contact');
      }
    } else {
      console.log('📧 [AUTO-SEND] No AI tags found in contact');
    }
    
    // 2. Fallback: Prüfe auch die alte aiTags Struktur (falls noch vorhanden)
    if (contact.aiTags && Array.isArray(contact.aiTags) && contact.aiTags.length > 0) {
      console.log('📧 [AUTO-SEND] Checking legacy aiTags structure:', contact.aiTags);
      const lastTag = contact.aiTags[contact.aiTags.length - 1];
      if (lastTag.selectedTopic) {
        console.log('📧 [AUTO-SEND] ✅ Using selectedTopic from legacy aiTags:', lastTag.selectedTopic);
        return lastTag.selectedTopic;
      }
    }
    
    console.log('📧 [AUTO-SEND] No AI tag topics found, using status-based logic');
    
    // 3. Fallback: Verwende die bisherige Status-basierte Logik
    const contactStatus = contact.status || contact.lead_status || contact.lead_contacted || "";
    console.log('📧 [AUTO-SEND] Contact status:', contactStatus);
    
    // Entscheidungslogik für das Topic basierend auf dem Status
    if (!contactStatus || contactStatus === "New" || contactStatus === "Import" || contactStatus === "Not Contacted") {
      console.log('📧 [AUTO-SEND] Status indicates "Introduce Yourself"');
      return "Introduce Yourself";
    } else if (contactStatus === "Introduced") {
      console.log('📧 [AUTO-SEND] Status indicates "Follow Up"');
      return "Follow Up";
    } else if (contactStatus === "First Follow-up" || contactStatus === "1. Follow-UP") {
      console.log('📧 [AUTO-SEND] Status indicates "Second Follow Up"');
      return "Second Follow Up";
    } else if (contactStatus === "Second Follow-up" || contactStatus === "2. Follow-UP") {
      console.log('📧 [AUTO-SEND] Status indicates "Third Follow Up"');
      return "Third Follow Up";
    } else {
      // Fallback für unbekannte Status
      console.log('📧 [AUTO-SEND] Unknown status, defaulting to "Introduce Yourself"');
      return "Introduce Yourself";
    }
  };

  // Hauptfunktion zum Starten des automatischen Prozesses
  const startAutoProcess = async () => {
    console.log("=== DEBUG: startAutoProcess gestartet ===");
    console.log("addCleaning:", addCleaning);
    console.log("bulkContacts:", bulkContacts);
    console.log("allData length:", allData ? allData.length : 'undefined');
    
    setIsConfirmed(true);
    setIsProcessing(true);
    setProgress(0);
    
    try {
      if (addCleaning) {
        console.log("Starting cleaning process...");
        setCurrentStep('cleaning');
        await performCleaning();
      } else {
        // Ohne Cleaning direkt zur E-Mail-Versendung
        // Lade die vollständigen Kontaktdaten aus allData basierend auf bulkContacts IDs
        console.log("=== DEBUG: Lade Kontakte ohne Cleaning ===");
        console.log("bulkContacts IDs:", bulkContacts);
        console.log("allData Länge:", allData ? allData.length : 'allData ist undefined');
        console.log("allData Sample (erste 2):", allData ? allData.slice(0, 2) : 'Keine Daten');
        
        // WICHTIGER DEBUG: Zeige die genaue Struktur der Daten
        if (allData && allData.length > 0) {
          console.log("=== DEBUG: Datenstruktur-Analyse ===");
          console.log("Erstes allData Element:", JSON.stringify(allData[0], null, 2));
          console.log("Verfügbare Eigenschaften:", Object.keys(allData[0]));
          console.log("E-Mail-Eigenschaften:", {
            lead_email: allData[0].lead_email,
            email: allData[0].email,
            lead_email_type: typeof allData[0].lead_email,
            lead_email_length: allData[0].lead_email ? allData[0].lead_email.length : 'N/A'
          });
        }
        
        if (bulkContacts && bulkContacts.length > 0) {
          console.log("=== DEBUG: bulkContacts Struktur ===");
          console.log("Erstes bulkContacts Element:", bulkContacts[0]);
          console.log("bulkContacts Element Type:", typeof bulkContacts[0]);
        }
        
        // WICHTIG: Prüfe, ob allData überhaupt Daten hat
        if (!allData || allData.length === 0) {
          throw new Error("allData ist leer oder undefined. Keine Kontaktdaten verfügbar.");
        }
        
        // WICHTIG: Prüfe, ob bulkContacts IDs hat
        if (!bulkContacts || bulkContacts.length === 0) {
          throw new Error("bulkContacts ist leer oder undefined. Keine Kontakt-IDs verfügbar.");
        }
        
        console.log("=== DEBUG: Detaillierte ID-Zuordnung ===");
        const contactsToSend = bulkContacts.map((contactId, index) => {
          console.log(`Suche Kontakt ${index + 1}: ID="${contactId}" (Type: ${typeof contactId})`);
          
          // Prüfe verschiedene ID-Formate
          const foundContact = allData.find(contact => {
            const match = contact.id === contactId || 
                         contact.key === contactId || 
                         String(contact.id) === String(contactId);
            if (match) {
              console.log(`✅ MATCH gefunden für ID ${contactId}:`, {
                id: contact.id,
                name: contact.business_name || contact.lead_name,
                email: contact.lead_email,
                hasEmail: !!contact.lead_email
              });
            }
            return match;
          });
          
          if (!foundContact) {
            console.warn(`❌ KEIN MATCH für ID ${contactId}. Verfügbare IDs in allData:`, 
              allData.slice(0, 5).map(c => ({ id: c.id, type: typeof c.id })));
          } else {
            console.log(`✅ Kontakt gefunden: ${foundContact.business_name || foundContact.lead_name || 'Unnamed'}, Email: ${foundContact.lead_email || 'Keine E-Mail'}`);
            
            // WICHTIGE VALIDIERUNG: Prüfe die Kontaktdaten
            if (!foundContact.lead_email || foundContact.lead_email.trim() === '') {
              console.warn(`⚠️ Kontakt ${foundContact.business_name || 'Unnamed'} hat keine E-Mail-Adresse!`);
            }
          }
          return foundContact;
        }).filter(contact => {
          const isValid = contact !== undefined && contact !== null;
          if (!isValid) {
            console.warn("❌ Ungültiger Kontakt gefiltert:", contact);
          }
          return isValid;
        }); // Entferne undefined Einträge
        
        console.log("=== DEBUG: Ergebnis nach Filterung ===");
        console.log("Contacts to send (without cleaning):", contactsToSend.length);
        console.log("Sample contact:", contactsToSend[0]);
        console.log("Alle gefilterten Kontakte:", contactsToSend.map(c => ({
          id: c.id,
          name: c.business_name || c.lead_name,
          email: c.lead_email,
          hasValidEmail: !!(c.lead_email && c.lead_email.trim())
        })));
        
        // WICHTIGE PRÜFUNG: Haben wir überhaupt Kontakte?
        if (contactsToSend.length === 0) {
          throw new Error(`Keine gültigen Kontakte gefunden! Von ${bulkContacts.length} IDs konnten 0 Kontakte in allData gefunden werden.`);
        }
        
        // WICHTIGE PRÜFUNG: Haben die Kontakte E-Mail-Adressen?
        const contactsWithEmail = contactsToSend.filter(c => c.lead_email && c.lead_email.trim());
        console.log(`=== DEBUG: E-Mail-Validierung ===`);
        console.log(`Kontakte mit E-Mail: ${contactsWithEmail.length}/${contactsToSend.length}`);
        
        if (contactsWithEmail.length === 0) {
          throw new Error(`Keine Kontakte mit gültigen E-Mail-Adressen gefunden! Alle ${contactsToSend.length} Kontakte haben leere oder fehlende E-Mail-Adressen.`);
        }
        
        console.log("=== DEBUG: Rufe performEmailSending auf ===");
        console.log("contactsToSend ist Array:", Array.isArray(contactsToSend));
        console.log("contactsToSend Länge:", contactsToSend.length);
        
        setCurrentStep('sending');
        await performEmailSending(contactsToSend);
      }
    } catch (error) {
      console.error("=== DEBUG: Fehler in startAutoProcess ===");
      console.error("Error in auto process:", error);
      console.error("Error stack:", error.stack);
      
      setResults(prev => ({
        ...prev,
        errors: [...prev.errors, {
          id: 'general',
          name: 'Process Error',
          error: error.message || 'Unknown error occurred'
        }]
      }));
      setCurrentStep('complete');
      setIsProcessing(false);
    }
  };

  // Separate Funktion für das Cleaning
  const performCleaning = async () => {
    setIsCleaningStep(true);
    setCleaningProgress(0);
    
    try {
      // Lade die vollständigen Kontaktdaten aus allData basierend auf bulkContacts IDs
      const contactsToClean = bulkContacts.map(contactId => {
        return allData.find(contact => contact.id === contactId);
      }).filter(contact => contact !== undefined); // Entferne undefined Einträge
      
      console.log("Contacts to clean:", contactsToClean.length);
      console.log("Sample contact data:", contactsToClean[0]);
      
      const cleaningResult = await cleanContacts(contactsToClean);
      setCleanedContacts(cleaningResult.cleanedContacts);
      setRemovedContacts(cleaningResult.removedContacts);
      setCleaningResults(cleaningResult.stats);
      
      setIsCleaningStep(false);
      setCleaningProgress(100); // Stelle sicher, dass Cleaning bei 100% steht
      
      // Bei Auto Cleaning Mode direkt mit E-Mail-Versand fortfahren
      if (autoCleaningMode) {
        if (cleaningResult.cleanedContacts.length > 0) {
          // Wechsle zum Sending-Step
          setCurrentStep('sending');
          setProgress(0); // Reset progress für E-Mail-Versand
          await performEmailSending(cleaningResult.cleanedContacts);
        } else {
          // Wenn keine Kontakte übrig sind, direkt zum Ergebnis-Screen
          setCurrentStep('complete');
          setIsProcessing(false);
        }
      } else {
        // Bei normalem Cleaning Mode zum Review-Screen
        setCurrentStep('cleaningResults');
        setIsProcessing(false);
      }
      
    } catch (error) {
      console.error("Error during cleaning:", error);
      setIsCleaningStep(false);
      setCurrentStep('complete');
      setIsProcessing(false);
      throw error;
    }
  };

  // Funktion um nach Cleaning-Review fortzufahren
  const proceedWithEmailSending = async (contactsToSend) => {
    setCurrentStep('sending');
    setIsProcessing(true);
    await performEmailSending(contactsToSend);
  };

  // Separate Funktion für E-Mail-Versendung
  const performEmailSending = async (contactsToProcess) => {
    console.log(`Starting email sending for ${contactsToProcess.length} contacts...`);
    console.log("DEBUG: contactsToProcess Array:", contactsToProcess);
    console.log("DEBUG: Erste 3 Kontakte:", contactsToProcess.slice(0, 3));
    
    const processResults = {
      total: contactsToProcess.length,
      success: 0,
      failed: 0,
      skipped: 0,
      errors: []
    };
    
    const sentEmailsList = [];
    setSentEmails([]); // Reset sent emails list
    
    for (let i = 0; i < contactsToProcess.length; i++) {
      const contact = contactsToProcess[i];
      
      // Verbesserte Sicherheitsprüfung für Kontakte
      if (!contact) {
        console.warn(`Kontakt an Index ${i} ist undefined oder null`);
        processResults.skipped++;
        processResults.errors.push({
          id: `invalid_contact_${i}`,
          name: 'Invalid Contact',
          error: 'Contact data is missing or invalid'
        });
        continue;
      }
      
      const contactId = contact.id || contact.key || `contact_${i}`;
      
      // Debug-Informationen
      console.log(`Verarbeite Kontakt ${i + 1}/${contactsToProcess.length}: ID=${contactId}, Name=${contact.business_name || contact.lead_name || 'Unbekannt'}, Email=${contact.lead_email || 'Keine E-Mail'}`);
      
      // Berechne den Fortschritt
      const currentProgress = Math.round(((i + 1) / contactsToProcess.length) * 100);
      setProgress(currentProgress);

      try {
        // 1. Prüfe, ob der Kontakt eine E-Mail-Adresse hat
        console.log(`DEBUG: Prüfe E-Mail für Kontakt ${contactId}:`);
        console.log(`  - lead_email: "${contact.lead_email}"`);
        console.log(`  - lead_email type: ${typeof contact.lead_email}`);
        console.log(`  - lead_email trimmed: "${contact.lead_email ? contact.lead_email.trim() : 'N/A'}"`);
        console.log(`  - Validation result: ${!contact.lead_email || contact.lead_email.trim() === ''}`);
        
        if (!contact.lead_email || contact.lead_email.trim() === '') {
          console.log(`❌ E-Mail übersprungen: Keine E-Mail-Adresse für ${contact.business_name || contact.lead_name || 'Unbekannt'}`);
          processResults.skipped++;
          processResults.errors.push({
            id: contactId,
            name: contact.business_name || contact.lead_name || 'Unbekannt',
            error: 'No email address found'
          });
          continue;
        }
        
        console.log(`✅ E-Mail-Adresse gefunden: ${contact.lead_email}`);

        // 2. Bestimme das passende Topic basierend auf dem Status
        const selectedTopic = await determineTopicForContact(contact);

        // 3. Erstelle Kontext für diesen Kontakt
        const contextInfo = `
Business Name: ${contact.business_name || ''}
City: ${contact.lead_city || ''}
Description: ${contact.lead_description || ''}
Type: ${contact.lead_type || ''}
Website: ${contact.lead_website || ''}
        `.trim();

        // 4. E-Mail generieren
        console.log(`DEBUG: Generiere E-Mail für ${contact.business_name || contact.lead_name || 'Unbekannt'} mit Topic: ${selectedTopic}`);
        const emailContent = await generateEmailForContact(
          contact, 
          selectedTopic, 
          selectedStyle, 
          contextInfo, 
          userId, 
          userSettings, 
          aiSettings, 
          userTopics, 
          topicTypeMapping, 
          getDefaultTopicNote
        );
        console.log(`✅ E-Mail generiert: Betreff="${emailContent.subject}", Body-Länge=${emailContent.body ? emailContent.body.length : 0}`);

        // 5. E-Mail-Adresse extrahieren und einrichten
        const emailAddresses = contact.lead_email.split(',').map(email => email.trim()).filter(Boolean);
        const primaryEmail = emailAddresses[0];
        const ccEmails = emailAddresses.slice(1);
        console.log(`DEBUG: E-Mail-Adressen - Primary: ${primaryEmail}, CC: ${ccEmails.join(', ')}`);

        // 6. E-Mail versenden
        console.log(`DEBUG: Sende E-Mail an ${primaryEmail}...`);
        const sendResult = await sendEmail(
          contact,
          primaryEmail,
          ccEmails,
          emailContent.subject,
          emailContent.body,
          selectedSenderEmail,
          selectedTopic
        );
        console.log(`DEBUG: Sendeergebnis:`, sendResult);

        if (sendResult.success) {
          processResults.success++;
          
          // 7. Kontaktstatus aktualisieren
          let newStatus = null;
          if (selectedTopic === "Introduce Yourself") {
            newStatus = "Introduced";
          } else if (selectedTopic === "Follow Up") {
            newStatus = "First Follow-up";
          } else if (selectedTopic === "Second Follow Up") {
            newStatus = "Second Follow-up";
          } else if (selectedTopic === "Third Follow Up") {
            newStatus = "Third Follow-up";
          }

          if (newStatus) {
            await updateContactStatus(userId, contactId, newStatus, onEmailResult);
          }
          
          // 🔥 NEU: Prüfe und setze AI Tag basierend auf dem verwendeten Topic
          await setAiTagAfterEmailSend(selectedTopic, contactId);
          
          // 8. Bei erfolgreichem Senden, rufe onEmailResult auf
          if (onEmailResult) {
            if (sendResult.delayed && sendResult.scheduledTime) {
              onEmailResult('success', `Reply scheduled for later`, contact, sendResult.scheduledTime, primaryEmail);
              console.log(`Email scheduled to be sent at: ${sendResult.scheduledTime} to: ${primaryEmail}`);
              
              // Füge die geplante E-Mail zur Liste hinzu
              sentEmailsList.push({
                id: contactId,
                name: contact.business_name || contact.lead_name || 'Unbekannt',
                email: primaryEmail,
                scheduledTime: sendResult.scheduledTime,
                isScheduled: true,
                topic: selectedTopic
              });
            } else {
              onEmailResult('success', `E-Mail to ${contact.business_name || contact.lead_name || 'Unbekannt'} sent successfully`, contact, null, primaryEmail);
              
              // Füge die sofort gesendete E-Mail zur Liste hinzu
              sentEmailsList.push({
                id: contactId,
                name: contact.business_name || contact.lead_name || 'Unbekannt',
                email: primaryEmail,
                scheduledTime: null,
                isScheduled: false,
                topic: selectedTopic
              });
            }
          }
        } else {
          processResults.failed++;
          processResults.errors.push({
            id: contactId,
            name: contact.business_name || contact.lead_name || 'Unbekannt',
            error: sendResult.error
          });
        }
      } catch (error) {
        console.error(`Error in contact ${contactId}:`, error);
        processResults.failed++;
        processResults.errors.push({
          id: contactId,
          name: contact.business_name || contact.lead_name || 'Unbekannt',
          error: error.message || 'Unknown error'
        });
      }
    }

    // Letzten Absender speichern
    if (useRandomSender) {
      // Speichere den Random-Sender-Status
      await set(ref(database, `/World/${userId}/settings/lastUsedSender`), {
        useRandomSender: true
      });
    } else {
      await saveLastSenderToDatabase(selectedSenderEmail, userId);
    }

    // Erfolgsmeldung an den Benutzer
    if (onEmailResult) {
      onEmailResult(
        processResults.failed === 0 ? 'success' : 'warning',
        `${processResults.success} emails successfully sent, ${processResults.failed} failed, ${processResults.skipped} skipped.`
      );
    }

    setSentEmails(sentEmailsList);
    setResults(processResults);
    setProcessComplete(true);
    setCurrentStep('complete');
    setIsProcessing(false);
    
  };

  // Hilfsfunktion zum Senden einer E-Mail
  const sendEmail = async (contact, primaryEmail, ccEmails, subject, bodyContent, senderEmail, selectedTopic) => {
    try {
      // Wähle eine zufällige Absender-E-Mail, wenn der Random-Modus aktiviert ist
      let actualSenderEmail = senderEmail;
      if (useRandomSender && senderEmails.length > 0) {
        const randomIndex = Math.floor(Math.random() * senderEmails.length);
        actualSenderEmail = senderEmails[randomIndex].email;
        console.log(`AutoSendMode - Verwende zufällige E-Mail-Adresse: ${actualSenderEmail}`);
      }
      
      // Absender-E-Mail finden
      const senderEmailData = senderEmails.find(item => item.email === actualSenderEmail);
      
      if (!senderEmailData) {
        throw new Error("The selected sender email was not found.");
      }
      
      // Debug-Logs für die Schlafzeiten
      console.log(`AutoSendMode - Sender Email: ${actualSenderEmail}, ID: ${senderEmailData.id}`);
      console.log(`AutoSendMode - Schlafzeiten: ${senderEmailData.sleep_time_start || 'nicht definiert'} bis ${senderEmailData.sleep_time_end || 'nicht definiert'}`);
      
      // E-Mail formatieren
      const formattedContent = convertToHtml(bodyContent, userSettings);
      let contentWithFooter = addFooterToEmail(formattedContent, userSettings);
      
      // Request-Daten erstellen
      const requestData = {
        recipients: [primaryEmail, ...ccEmails],
        cc: ccEmails,
        subject: subject || '',
        content: contentWithFooter || '',
        userId: userId,
        businessName: contact.business_name || contact.lead_name || 'Unbekannt',
        leadId: contact.id,
        emailType: topicTypeMapping[selectedTopic] || "",
        senderEmail: senderEmailData.email.trim(),
        senderPassword: senderEmailData.password,
        senderName: senderEmailData.sender_name || userSettings.user_name || 'BonusClub',
        imapHost: senderEmailData.imap_host,
        imapPort: senderEmailData.imap_port,
        smtpHost: senderEmailData.smtp_host,
        smtpPort: senderEmailData.smtp_port,
        encryptionType: senderEmailData.encryption_type,
        smtpEncryption: String(senderEmailData.smtp_encryption),
        emailId: senderEmailData.id
      };
      
      console.log(`AutoSendMode - Sende E-Mail-Anfrage mit emailId: ${senderEmailData.id}`);
      
      // E-Mail senden
      const response = await fetch(CLOUD_FUNCTION_URLS.sendWorldEmail(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });
      
      const responseText = await response.text();
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error('Fehler beim Parsen der JSON-Antwort:', e);
        return { 
          success: false, 
          error: `Server-Antwort ist kein gültiges JSON: ${responseText ? responseText.substring(0, 100) : 'Keine Antwort'}...` 
        };
      }
      
      if (data.success) {
        // Prüfe, ob die E-Mail verzögert wurde und eine geplante Zeit hat
        if (data.delayed && data.scheduledTime) {
          // Zusätzliche Logs für Schlafzeiten
          if (data.sleepTimeAdjusted) {
            console.log(`AutoSendMode - E-Mail wurde wegen Schlafzeit angepasst: ${data.sleepTimeStart} bis ${data.sleepTimeEnd}`);
            console.log(`AutoSendMode - Ursprüngliche Zeit: ${data.originalScheduledTime}, Neue Zeit: ${data.scheduledTime}`);
          }
          
          // scheduledTime in einen gültigen Timestamp konvertieren
          let scheduledTimeStamp = null;
          try {
            scheduledTimeStamp = new Date(data.scheduledTime).getTime();
            console.log(`Geplante Zeit konvertiert: ${data.scheduledTime} -> ${scheduledTimeStamp}`);
          } catch (e) {
            console.error("Fehler bei der Konvertierung der geplanten Zeit:", e);
          }
          
          // Erfolg mit verzögerter Sendung
          return { 
            success: true, 
            delayed: true,
            scheduledTime: scheduledTimeStamp
          };
        }
        // Normaler Erfolg
        return { success: true };
      } else {
        let errorMessage = data.error || 'Unknown error in sending email';
        
        if (data.error && data.error.includes("Sender address rejected")) {
          errorMessage = `Die E-Mail-Adresse "${senderEmailData.email}" wurde vom Server abgelehnt.`;
        }
        
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      console.error("Fehler beim Senden der E-Mail:", error);
      return { success: false, error: error.message || 'Unknown error in sending email' };
    }
  };

  // Hilfsfunktion für Standard-Topic-Anweisungen
  const getDefaultTopicNote = (topicType) => {
    const defaultNotes = {
      'intro': "Bitte stelle unser Unternehmen vor, teile einige spannende Details darüber, wie wir helfen können, und füge einen Call-to-Action hinzu.",
      'follow_up_1': "Erkundige dich höflich, ob der Empfänger die vorherige E-Mail erhalten hat. Halte es kurz und freundlich.",
      'follow_up_2': "Dies ist das zweite Follow-up. Frage höflich nach, ob der Empfänger noch interessiert ist, ohne aufdringlich zu sein.",
      'follow_up_3': "Dies ist das letzte Follow-up. Mache einen letzten sanften Versuch, den Empfänger zu erreichen.",
      'custom_reply': "Bitte verfasse eine durchdachte Antwort auf die Nachricht des Empfängers."
    };
    
    return defaultNotes[topicType] || "Bitte schreibe eine professionelle E-Mail an den Empfänger.";
  };

  // Analysiere die Kontakte, um eine Übersicht über die Topics zu erstellen
  const generateTopicSummary = async () => {
    if (!bulkContacts || !allData) return {};
    
    console.log('📧 [AUTO-SEND-SUMMARY] === GENERATING TOPIC SUMMARY ===');
    console.log('📧 [AUTO-SEND-SUMMARY] Total contacts to analyze:', bulkContacts.length);
    
    const summary = {
      "Introduce Yourself": 0,
      "Follow Up": 0,
      "Second Follow Up": 0,
      "Third Follow Up": 0
    };
    
    const detailedBreakdown = [];
    
    // 🔥 WICHTIG: Verwende die GLEICHE Logik wie determineTopicForContact (mit Datenbankabfrage)
    for (let index = 0; index < bulkContacts.length; index++) {
      const contactId = bulkContacts[index];
      const contact = allData.find(row => row.id === contactId);
      
      if (contact) {
        console.log(`📧 [AUTO-SEND-SUMMARY] Contact ${index + 1}/${bulkContacts.length}: ${contact.business_name || 'Unnamed'} (${contactId})`);
        
        // 🔥 VERWENDE DIE GLEICHE LOGIK WIE IN determineTopicForContact
        let topic = "Introduce Yourself"; // Default
        let reason = "Default (no status)";
        
        // 1. Prüfe AI Tags mit Datenbankabfrage (GLEICHE LOGIK!)
        if (contact.tags && typeof contact.tags === 'object') {
          console.log('📧 [AUTO-SEND-SUMMARY]   Has AI Tags:', Object.keys(contact.tags));
          
          const tagsArray = Object.entries(contact.tags).map(([tagId, tagData]) => ({
            id: tagId,
            ...tagData
          }));
          
          tagsArray.sort((a, b) => {
            const dateA = new Date(a.appliedAt || 0);
            const dateB = new Date(b.appliedAt || 0);
            return dateB - dateA;
          });
          
          if (tagsArray.length > 0) {
            const newestTag = tagsArray[0];
            console.log('📧 [AUTO-SEND-SUMMARY]   Newest Tag:', {
              id: newestTag.id,
              selectedTopic: newestTag.selectedTopic,
              appliedTopic: newestTag.appliedTopic,
              appliedAt: newestTag.appliedAt
            });
            
            // 🔥 DATENBANKABFRAGE: Lade AI Tag Settings (GLEICHE LOGIK!)
            if (newestTag.id) {
              try {
                console.log('📧 [AUTO-SEND-SUMMARY]   Loading AI Tag settings from database for ID:', newestTag.id);
                
                const aiTagRef = ref(database, `/World/${userId}/settings/aiTags/${newestTag.id}`);
                const aiTagSnapshot = await new Promise((resolve) => {
                  onValue(aiTagRef, resolve, { onlyOnce: true });
                });
                
                if (aiTagSnapshot.exists()) {
                  const aiTagData = aiTagSnapshot.val();
                  console.log('📧 [AUTO-SEND-SUMMARY]   ✅ Loaded AI Tag settings from database:', {
                    selectedTopic: aiTagData.selectedTopic,
                    tag: aiTagData.tag,
                    name: aiTagData.name
                  });
                  
                  if (aiTagData.selectedTopic) {
                    topic = aiTagData.selectedTopic;
                    reason = `AI Tag from DB: ${aiTagData.tag || aiTagData.name} (${aiTagData.selectedTopic})`;
                    console.log('📧 [AUTO-SEND-SUMMARY]   ✅ Using selectedTopic from database:', topic);
                  }
                } else {
                  console.log('📧 [AUTO-SEND-SUMMARY]   ⚠️ AI Tag settings not found in database for ID:', newestTag.id);
                }
              } catch (error) {
                console.error('📧 [AUTO-SEND-SUMMARY]   Error loading AI Tag settings:', error);
              }
            }
            
            // Fallback: Gespeichertes selectedTopic vom Kontakt
            if (topic === "Introduce Yourself" && newestTag.selectedTopic) {
              topic = newestTag.selectedTopic;
              reason = `AI Tag stored: ${newestTag.tag || newestTag.name || newestTag.id} (${newestTag.selectedTopic})`;
              console.log('📧 [AUTO-SEND-SUMMARY]   ✅ Using stored selectedTopic:', topic);
            }
          }
        } else {
          console.log('📧 [AUTO-SEND-SUMMARY]   No AI Tags found');
        }
        
        // 2. Fallback auf Status-basierte Logik (wenn kein AI Tag Topic gefunden)
        if (topic === "Introduce Yourself" && reason === "Default (no status)") {
          const contactStatus = contact.status || contact.lead_status || contact.lead_contacted || "";
          console.log('📧 [AUTO-SEND-SUMMARY]   Using status-based logic, status:', contactStatus);
          
          if (!contactStatus || contactStatus === "New" || contactStatus === "Import" || contactStatus === "Not Contacted") {
            topic = "Introduce Yourself";
            reason = `Status: ${contactStatus || 'No status'} → Introduce`;
          } else if (contactStatus === "Introduced") {
            topic = "Follow Up";
            reason = `Status: ${contactStatus} → Follow Up`;
          } else if (contactStatus === "First Follow-up" || contactStatus === "1. Follow-UP") {
            topic = "Second Follow Up";
            reason = `Status: ${contactStatus} → Second Follow Up`;
          } else if (contactStatus === "Second Follow-up" || contactStatus === "2. Follow-UP") {
            topic = "Third Follow Up";
            reason = `Status: ${contactStatus} → Third Follow Up`;
          } else {
            topic = "Introduce Yourself";
            reason = `Status: ${contactStatus} → Default (Introduce)`;
          }
        }
        
        console.log(`📧 [AUTO-SEND-SUMMARY]   🎯 FINAL TOPIC: ${topic} (${reason})`);
        
        summary[topic] = (summary[topic] || 0) + 1;
        detailedBreakdown.push({
          contact: contact.business_name || 'Unnamed',
          contactId,
          topic,
          reason,
          hasAiTags: !!(contact.tags && Object.keys(contact.tags).length),
          status: contact.status || contact.lead_status || contact.lead_contacted || 'No status'
        });
      } else {
        console.log(`📧 [AUTO-SEND-SUMMARY] ❌ Contact not found for ID: ${contactId}`);
      }
    }
    
    console.log('📧 [AUTO-SEND-SUMMARY] === SUMMARY RESULTS ===');
    Object.entries(summary).forEach(([topic, count]) => {
      if (count > 0) {
        console.log(`📧 [AUTO-SEND-SUMMARY] ${topic}: ${count} contacts`);
      }
    });
    
    console.log('📧 [AUTO-SEND-SUMMARY] === DETAILED BREAKDOWN ===');
    detailedBreakdown.forEach((item, index) => {
      console.log(`📧 [AUTO-SEND-SUMMARY] ${index + 1}. ${item.contact} → ${item.topic} (${item.reason})`);
    });
    
    return summary;
  };
  
  // Formatiere das Datum für die Anzeige
  const formatScheduledDate = (timestamp) => {
    if (!timestamp) return 'Sent immediately';
    
    try {
      const date = new Date(parseInt(timestamp));
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      
      // Englische Monatsnamen
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      
      const day = date.getDate();
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      
      return `${month} ${day}, ${year}, ${hours}:${minutes}h`;
    } catch (error) {
      console.error('Error formatting scheduled date:', error);
      return 'Invalid date';
    }
  };

  // Übersetzt die Topic-Namen ins Deutsche für die Anzeige
  const translateTopicName = (topicName) => {
    const translations = {
      "Introduce Yourself": "Introduction",
      "Follow Up": "First Follow-up",
      "Second Follow Up": "Second Follow-up",
      "Third Follow Up": "Third Follow-up"
    };
    
    return translations[topicName] || topicName;
  };

  // Funktion zum Cleanen der Kontakte
  const cleanContacts = async (contactsToClean) => {
    setIsCleaningStep(true);
    setCleaningProgress(0);
    
    try {
      // Lade AI Settings für Cleaning
      const aiSettingsRef = ref(database, `/World/${userId}/settings/ai`);
      const snapshot = await get(aiSettingsRef);
      
      if (!snapshot.exists() || !snapshot.val().cleanupGuidelines) {
        throw new Error("Cleanup rules not configured. Please set up cleanup guidelines in settings first.");
      }
      
      const guidelines = snapshot.val().cleanupGuidelines;
      const toKeep = [];
      const toRemove = [];
      const toRename = [];
      
      for (let i = 0; i < contactsToClean.length; i++) {
        const contact = contactsToClean[i];
        
        // Berechne den Fortschritt
        const currentProgress = Math.round(((i + 1) / contactsToClean.length) * 100);
        setCleaningProgress(currentProgress);
        
        // Prüfe, ob die Kontaktdaten für die Analyse ausreichen
        if (!contact || (!contact.business_name && !contact.lead_description)) {
          toRemove.push({
            ...contact,
            reason: "Insufficient data for analysis",
            originalName: contact.business_name || 'Unnamed',
            email: contact.lead_email || 'No email'
          });
          continue;
        }
        
        try {
          // Verwende die Cloud Function für die Analyse
          const response = await fetch(CLOUD_FUNCTION_URLS.cleanupContact(), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              businessName: contact.business_name || '',
              description: contact.lead_description || '',
              guidelines: guidelines
            })
          });
          
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          
          const result = await response.json();
          
          if (result.shouldKeep) {
            // Wenn der Business-Name geändert werden soll
            if (result.cleanedBusinessName && result.cleanedBusinessName !== contact.business_name) {
              toRename.push({
                ...contact,
                originalName: contact.business_name,
                cleanedName: result.cleanedBusinessName,
                reason: result.reason || "Name cleaned",
                email: contact.lead_email || 'No email'
              });
            } else {
              toKeep.push({
                ...contact,
                reason: result.reason || "Meets criteria",
                originalName: contact.business_name || 'Unnamed',
                email: contact.lead_email || 'No email'
              });
            }
          } else {
            toRemove.push({
              ...contact,
              reason: result.reason || "Does not meet criteria",
              originalName: contact.business_name || 'Unnamed',
              email: contact.lead_email || 'No email'
            });
          }
        } catch (error) {
          console.error(`Error analyzing contact ${contact.id}:`, error);
          // Bei Fehler behalten wir den Kontakt
          toKeep.push({
            ...contact,
            reason: `Analysis error: ${error.message}`,
            originalName: contact.business_name || 'Unnamed',
            email: contact.lead_email || 'No email'
          });
        }
      }
      
      // Wende die Cleaning-Änderungen in der Datenbank an
      const batch = writeBatch(firestore);
      
      // Verarbeite Umbenennungen
      toRename.forEach(contact => {
        const contactRef = doc(firestore, `World/${userId}/db/${contact.id}`);
        const updatedDescription = contact.lead_description 
          ? `${contact.lead_description}\n\nOriginal Business Name: ${contact.originalName}`
          : `Original Business Name: ${contact.originalName}`;
        
        batch.update(contactRef, {
          business_name: contact.cleanedName,
          lead_description: updatedDescription
        });
      });
      
      // Verarbeite Löschungen
      toRemove.forEach(contact => {
        const contactRef = doc(firestore, `World/${userId}/db/${contact.id}`);
        batch.delete(contactRef);
      });
      
      // Committe den Batch nur, falls es tatsächlich Schreibvorgänge gibt
      if (toRename.length > 0 || toRemove.length > 0) {
        await batch.commit();
      }
      
      // Sammle alle Kontakte, die behalten werden sollen (inkl. umbenannte)
      const finalCleanedContacts = [
        ...toKeep,
        ...toRename.map(contact => ({
          ...contact,
          business_name: contact.cleanedName,
          lead_description: contact.lead_description 
            ? `${contact.lead_description}\n\nOriginal Business Name: ${contact.originalName}`
            : `Original Business Name: ${contact.originalName}`
        }))
      ];
      
      setCleanedContacts(finalCleanedContacts);
      setCleaningResults({
        kept: toKeep.length,
        renamed: toRename.length,
        removed: toRemove.length,
        total: contactsToClean.length,
        // Detaillierte Listen für die Tabs
        keptContacts: toKeep,
        renamedContacts: toRename,
        removedContacts: toRemove,
        allContacts: [...toKeep, ...toRename, ...toRemove]
      });
      
      setIsCleaningStep(false);
      return {
        cleanedContacts: finalCleanedContacts,
        removedContacts: toRemove,
        stats: {
          kept: toKeep.length,
          renamed: toRename.length,
          removed: toRemove.length,
          total: contactsToClean.length,
          keptContacts: toKeep,
          renamedContacts: toRename,
          removedContacts: toRemove,
          allContacts: [...toKeep, ...toRename, ...toRemove]
        }
      };
      
    } catch (error) {
      console.error("Error during cleaning:", error);
      setIsCleaningStep(false);
      throw error;
    }
  };

  // Hilfsfunktion zum sicheren Erstellen von Kontakt-Updates (filtert undefined-Werte)
  const createSafeContactUpdate = (contact) => {
    const update = {};
    
    // Nur definierte Werte hinzufügen
    if (contact.business_name !== undefined) update.business_name = contact.business_name;
    else if (contact.originalName !== undefined) update.business_name = contact.originalName;
    else update.business_name = 'Unnamed Business';
    
    if (contact.lead_email !== undefined) update.lead_email = contact.lead_email;
    else if (contact.email !== undefined) update.lead_email = contact.email;
    else update.lead_email = '';
    
    if (contact.lead_city !== undefined) update.lead_city = contact.lead_city;
    else update.lead_city = '';
    
    if (contact.lead_description !== undefined) update.lead_description = contact.lead_description;
    else update.lead_description = '';
    
    if (contact.lead_type !== undefined) update.lead_type = contact.lead_type;
    else update.lead_type = '';
    
    if (contact.lead_website !== undefined) update.lead_website = contact.lead_website;
    else update.lead_website = '';
    
    if (contact.status !== undefined) update.status = contact.status;
    else update.status = 'New';
    
    return update;
  };

  const handleRestoreRemovedContacts = async () => {
    try {
      const restoreBatch = writeBatch(firestore);
      removedContacts.forEach(contact => {
        const contactRef = doc(firestore, `World/${userId}/db/${contact.id}`);
        restoreBatch.set(contactRef, createSafeContactUpdate(contact), { merge: true });
      });
      await restoreBatch.commit();
      
      // Zeige Erfolgsmeldung mit 'summary' Status für korrekte Toast-Anzeige
      if (onEmailResult) {
        onEmailResult('summary', `Contacts Restored: ${removedContacts.length} contacts restored to your database.`, null, null, null);
      }
      
      // Schließe das Modal und kehre zur Tabelle zurück
      onClose();
      
    } catch (error) {
      console.error("Error restoring contacts:", error);
      if (onEmailResult) {
        onEmailResult('error', 'Restore Failed: Failed to restore contacts. Please try again.', null, null, null);
      }
    }
  };

  const handleKeepContactsDeleted = () => {
    // Zeige Bestätigungsmeldung mit 'summary' Status für korrekte Toast-Anzeige
    if (onEmailResult) {
      onEmailResult('summary', `Contacts Deleted: ${removedContacts.length} contacts permanently removed from your database.`, null, null, null);
    }
    
    // Schließe das Modal und kehre zur Tabelle zurück
    onClose();
  };

  // 🔥 NEUE FUNKTION: AI Tag basierend auf Topic nach E-Mail-Versand setzen
  const setAiTagAfterEmailSend = async (topic, contactId) => {
    try {
      console.log('🏷️ [POST-EMAIL-TAG] Checking if topic has associated AI Tag:', topic);
      
      if (!userId || !contactId || !topic) {
        console.log('🏷️ [POST-EMAIL-TAG] Missing required parameters');
        return false;
      }
      
      // Erweiterte Suche: Prüfe sowohl in selectedTopics als auch in customTopics
      let matchingTopic = null;
      
      // 1. Suche in allen userTopics (sowohl selectedTopics als auch customTopics)
      matchingTopic = Object.values(userTopics).find(topicData => {
        return topicData.topic === topic || topicData.type === topic;
      });
      
      // 2. Falls nicht gefunden, erweiterte Suche in der Struktur
      if (!matchingTopic && userTopics.selectedTopics) {
        matchingTopic = Object.values(userTopics.selectedTopics).find(topicData => {
          return topicData.topic === topic || topicData.type === topic;
        });
      }
      
      // 3. Falls immer noch nicht gefunden, suche in customTopics
      if (!matchingTopic && userTopics.customTopics) {
        matchingTopic = Object.values(userTopics.customTopics).find(topicData => {
          return topicData.topic === topic || topicData.type === topic;
        });
      }
      
      if (!matchingTopic) {
        console.log('🏷️ [POST-EMAIL-TAG] No matching topic found in userTopics for:', topic);
        console.log('🏷️ [POST-EMAIL-TAG] Available topics:', {
          allTopics: Object.values(userTopics).map(t => ({ topic: t.topic, type: t.type, hasAiTagId: !!t.aiTagId })),
          selectedTopics: userTopics.selectedTopics ? Object.keys(userTopics.selectedTopics) : 'none',
          customTopics: userTopics.customTopics ? Object.keys(userTopics.customTopics) : 'none'
        });
        return false;
      }
      
      console.log('🏷️ [POST-EMAIL-TAG] Found matching topic:', matchingTopic);
      
      // Prüfe, ob das Topic einen zugeordneten AI Tag hat
      if (!matchingTopic.aiTagId) {
        console.log('🏷️ [POST-EMAIL-TAG] Topic has no associated AI tag - this is normal');
        return false;
      }
      
      console.log('🏷️ [POST-EMAIL-TAG] ✅ Topic has associated AI tag ID:', matchingTopic.aiTagId);
      
      // Lade die AI Tag-Einstellungen
      const aiTagRef = ref(database, `/World/${userId}/settings/aiTags/${matchingTopic.aiTagId}`);
      const aiTagSnapshot = await new Promise((resolve) => {
        onValue(aiTagRef, resolve, { onlyOnce: true });
      });
      
      if (!aiTagSnapshot.exists()) {
        console.log('🏷️ [POST-EMAIL-TAG] AI tag not found for ID:', matchingTopic.aiTagId);
        return false;
      }
      
      const aiTagData = aiTagSnapshot.val();
      console.log('🏷️ [POST-EMAIL-TAG] AI tag data loaded:', aiTagData);
      
      // Erstelle den neuen Tag für den Kontakt
      const newTag = {
        tag: aiTagData.tag,
        name: aiTagData.name,
        color: aiTagData.color,
        icon: aiTagData.icon,
        appliedAt: new Date().toISOString(),
        appliedBy: 'post_email_send',
        appliedTopic: topic,
        ...(aiTagData.selectedTopic && { selectedTopic: aiTagData.selectedTopic })
      };
      
      // Prüfe, ob es ein Overwrite-Tag ist
      const contactDocRef = doc(firestore, `World/${userId}/db/${contactId}`);
      if (aiTagData.overwriteOtherTags === true) {
        console.log('🏷️ [POST-EMAIL-TAG] Overwrite tag detected, removing all other tags');
        
        await updateDoc(contactDocRef, { tags: { [matchingTopic.aiTagId]: newTag } });
      } else {
        console.log('🏷️ [POST-EMAIL-TAG] Adding tag alongside existing tags');
        
        const contactSnap = await getDoc(contactDocRef);
        const existingTags = contactSnap.exists() ? contactSnap.data().tags || {} : {};
        await updateDoc(contactDocRef, { [`tags.${matchingTopic.aiTagId}`]: newTag, tags: { ...existingTags, [matchingTopic.aiTagId]: newTag } });
      }
      
      console.log('🏷️ [POST-EMAIL-TAG] ✅ AI tag successfully applied after email send');
      return true;
      
    } catch (error) {
      console.error('🏷️ [POST-EMAIL-TAG] Error setting AI tag after email send:', error);
      return false;
    }
  };

  // Render Bestätigungsbildschirm
  const renderConfirmationScreen = () => {
    return (
      <div className="as-confirmation">
        <div className="as-summary">
          <h3 className="as-summary-title">Planned Emails</h3>
          <div className="as-grid">
            {Object.entries(topicSummary).map(([topic, count]) => (
              count > 0 && (
                <div key={topic} className="as-item">
                  <span className="as-count">{count}</span>
                  <span className="as-label">{topic}</span>
                </div>
              )
            ))}
          </div>
      
        </div>

        <div className="as-settings">
          <div className="as-group">
            <label className="as-label">Style:</label>
            <div className="as-button-group">
              <button
                className={`as-button ${selectedStyle === "Professional & Supportive" ? "active" : ""}`}
                onClick={() => setSelectedStyle("Professional & Supportive")}
              >
                Professional
              </button>
              <button
                className={`as-button ${selectedStyle === "Friendly & Authentic" ? "active" : ""}`}
                onClick={() => setSelectedStyle("Friendly & Authentic")}
              >
                Casual
              </button>
            </div>
          </div>

          <div className="as-group">
            <label className="as-label">Sender Email:</label>
            <div className="as-sender-selection">
              {senderEmails.length > 0 ? (
                <div className="as-sender-options">
                  {/* Random Rotation Option */}
                  <div 
                    className={`as-sender-choice ${useRandomSender ? 'as-selected' : ''}`}
                    onClick={() => {
                      setUseRandomSender(true);
                      setSelectedSenderEmail("");
                    }}
                  >
                    <div className="as-sender-choice-content">
                      <div className="as-sender-choice-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="23 4 23 10 17 10"></polyline>
                          <polyline points="1 20 1 14 7 14"></polyline>
                          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                        </svg>
                      </div>
                      <div className="as-sender-choice-text">
                        <div className="as-sender-choice-header">
                          <span className="as-sender-choice-title">Random Rotation</span>
                        </div>
                        <div className="as-sender-choice-description">
                          Automatically rotate between all {senderEmails.length} configured email addresses
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Individual Email Options */}
                  {senderEmails.map((item) => (
                    <div 
                      key={item.email}
                      className={`as-sender-choice ${!useRandomSender && selectedSenderEmail === item.email ? 'as-selected' : ''}`}
                      onClick={() => {
                        setUseRandomSender(false);
                        setSelectedSenderEmail(item.email);
                      }}
                    >
                      <div className="as-sender-choice-content">
                        <div className="as-sender-choice-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                            <polyline points="22,6 12,13 2,6"></polyline>
                          </svg>
                        </div>
                        <div className="as-sender-choice-text">
                          <div className="as-sender-choice-header">
                            <span className="as-sender-choice-title">
                              {item.sender_name || 'Email Account'}
                            </span>
                          </div>
                          <div className="as-sender-choice-description">
                            {item.email}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="as-no-senders">
                  <p>No sender emails configured. Please add sender emails in your settings.</p>
                </div>
              )}
            </div>
          </div>

          <div className="as-group">
            <label className="as-label">Cleaning Options:</label>
            <div className="as-cleaning-options">
              <div 
                className={`as-radio-option ${!addCleaning ? 'as-selected' : ''}`}
                onClick={() => {
                  setAddCleaning(false);
                  setAutoCleaningMode(false);
                }}
              >
                <div className="as-radio-content">
                  <div className="as-option-title">No Cleaning</div>
                  <div className="as-option-description">Send emails to all selected contacts without cleaning</div>
                </div>
              </div>
              
              <div 
                className={`as-radio-option ${addCleaning && !autoCleaningMode ? 'as-selected' : ''}`}
                onClick={() => {
                  setAddCleaning(true);
                  setAutoCleaningMode(false);
                }}
              >
                <div className="as-radio-content">
                  <div className="as-option-title">Enable Cleaning</div>
                  <div className="as-option-description">Review cleaning results before sending emails</div>
                </div>
              </div>
              
              <div 
                className={`as-radio-option ${addCleaning && autoCleaningMode ? 'as-selected' : ''}`}
                onClick={() => {
                  setAddCleaning(true);
                  setAutoCleaningMode(true);
                }}
              >
                <div className="as-radio-content">
                  <div className="as-option-title">Enable Auto Cleaning</div>
                  <div className="as-option-description">Automatically send emails to cleaned contacts without review</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="as-warning">
          {!addCleaning && `You are about to send ${bulkContacts.length} emails. This process may take some time.`}
          {addCleaning && !autoCleaningMode && `You are about to clean and send ${bulkContacts.length} emails. Contacts will be analyzed first - some may be removed if they don't meet your criteria.`}
          {addCleaning && autoCleaningMode && `You are about to auto-clean and send ${bulkContacts.length} emails. Contacts will be automatically cleaned and emails sent to qualifying contacts without review.`}
        </div>

        <div className="as-actions">
          <button 
            className="as-cancel" 
            onClick={handleClose}
          >
            Cancel
          </button>
          <button 
            className="as-confirm" 
            onClick={startAutoProcess}
            disabled={(!selectedSenderEmail && !useRandomSender) || bulkContacts.length === 0}
          >
            Start AutoSend
          </button>
        </div>
      </div>
    );
  };

  // Render Fortschrittsbildschirm
  const renderProgressScreen = () => {
    if (currentStep === 'cleaning' || isCleaningStep) {
      return (
        <div className="as-progress">
          <h2 className="as-section-title">Cleaning Contacts</h2>
          
          <div className="as-progress-bar-container">
            <div 
              className="as-progress-bar" 
              style={{ width: `${cleaningProgress}%` }}
            ></div>
          </div>
          
          <p className="as-progress-status">{cleaningProgress}% completed</p>
          <p className="as-progress-details">
            {Math.round((cleaningProgress / 100) * bulkContacts.length)} of {bulkContacts.length} contacts analyzed
          </p>
          <p className="as-progress-note">
            AI is analyzing and cleaning your contacts based on your cleanup rules.
          </p>
        
        </div>
      );
    }
    
    if (currentStep === 'sending') {
      // Bestimme die korrekte Anzahl der zu sendenden Kontakte
      let contactsToSendCount = bulkContacts.length;
      let contactsProcessedCount = 0;
      
      if (addCleaning && cleanedContacts.length > 0) {
        // Nach dem Cleaning verwende die Anzahl der bereinigten Kontakte
        contactsToSendCount = cleanedContacts.length;
        contactsProcessedCount = Math.round((progress / 100) * cleanedContacts.length);
      } else {
        // Ohne Cleaning verwende die ursprüngliche Anzahl
        contactsProcessedCount = Math.round((progress / 100) * bulkContacts.length);
      }
      
      return (
        <div className="as-progress">
          <h2 className="as-section-title">Sending Emails</h2>
          
          {addCleaning && cleaningResults && (
            <div className="as-cleaning-summary">
              <p className="as-cleaning-results">
                Cleaning completed: {cleaningResults.kept} kept, {cleaningResults.renamed} renamed, {cleaningResults.removed} removed
              </p>
            </div>
          )}
          
          <div className="as-progress-bar-container">
            <div 
              className="as-progress-bar" 
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          
          <p className="as-progress-status">{progress}% completed</p>
          <p className="as-progress-details">
            {contactsProcessedCount} of {contactsToSendCount} emails processed
          </p>
          <p className="as-progress-note">
            Please be patient while we generate and send your emails.
          </p>
      
        </div>
      );
    }
    
    return null;
  };

  // Render Cleaning-Ergebnisse Bildschirm
  const renderCleaningResultsScreen = () => {
    const handleProceedWithCleaned = () => {
      proceedWithEmailSending(cleanedContacts);
    };

    // Funktion zum Rendern der Kontaktliste basierend auf dem aktiven Tab
    const renderContactList = () => {
      if (!cleaningResults) return null;

      let contactsToShow = [];
      let emptyMessage = '';

      switch (activeTab) {
        case 'all':
          contactsToShow = cleaningResults.allContacts || [];
          emptyMessage = 'No contacts processed';
          break;
        case 'kept':
          contactsToShow = cleaningResults.keptContacts || [];
          emptyMessage = 'No contacts were kept unchanged';
          break;
        case 'renamed':
          contactsToShow = cleaningResults.renamedContacts || [];
          emptyMessage = 'No contacts were renamed';
          break;
        case 'removed':
          contactsToShow = cleaningResults.removedContacts || [];
          emptyMessage = 'No contacts were removed';
          break;
        default:
          contactsToShow = [];
      }

      if (contactsToShow.length === 0) {
        return (
          <div className="as-empty-tab-message">
            <p>{emptyMessage}</p>
          </div>
        );
      }

      return (
        <div className="as-contact-details-list">
          {contactsToShow.map((contact, index) => (
            <div key={index} className={`as-contact-detail-item as-${activeTab === 'all' ? getContactType(contact) : activeTab}`}>
              <div className="as-contact-detail-header">
                <div className="as-contact-detail-info">
                  <span className="as-contact-detail-name">
                    {activeTab === 'renamed' || (activeTab === 'all' && contact.cleanedName) ? (
                      <>
                        <span className="as-original-name">{contact.originalName}</span>
                        <span className="as-arrow">→</span>
                        <span className="as-cleaned-name">{contact.cleanedName}</span>
                      </>
                    ) : (
                      contact.originalName || contact.business_name || 'Unnamed'
                    )}
                  </span>
                  <span className="as-contact-detail-email">{contact.email}</span>
                </div>
                <div className="as-contact-detail-status">
                  {activeTab === 'all' && (
                    <span className={`as-status-badge as-${getContactType(contact)}`}>
                      {getContactTypeLabel(contact)}
                    </span>
                  )}
                </div>
              </div>
              <div className="as-contact-detail-reason">
                <span className="as-reason-label">Reason:</span>
                <span className="as-reason-text">{contact.reason}</span>
              </div>
            </div>
          ))}
        </div>
      );
    };

    // Hilfsfunktion um den Kontakttyp zu bestimmen
    const getContactType = (contact) => {
      if (contact.cleanedName) return 'renamed';
      if (cleaningResults.removedContacts?.includes(contact)) return 'removed';
      return 'kept';
    };

    // Hilfsfunktion für Kontakttyp-Labels
    const getContactTypeLabel = (contact) => {
      const type = getContactType(contact);
      switch (type) {
        case 'kept': return 'Kept';
        case 'renamed': return 'Renamed';
        case 'removed': return 'Removed';
        default: return 'Unknown';
      }
    };

    return (
      <div className="as-cleaning-results-screen">
        <h2 className="as-section-title">Cleaning Results</h2>
        
        <div className="as-autoresult-cleaning-summary">
          <h3 className="as-autoresult-title">Cleaning Results</h3>
          <div className="as-autoresult-stats-container">
            <div className="as-autoresult-stat-item as-autoresult-kept">
              <span className="as-autoresult-number">{cleaningResults?.kept || 0}</span>
              <span className="as-autoresult-label">Kept</span>
            </div>
            <div className="as-autoresult-stat-item as-autoresult-renamed">
              <span className="as-autoresult-number">{cleaningResults?.renamed || 0}</span>
              <span className="as-autoresult-label">Renamed</span>
            </div>
            <div className="as-autoresult-stat-item as-autoresult-removed">
              <span className="as-autoresult-number">{cleaningResults?.removed || 0}</span>
              <span className="as-autoresult-label">Removed</span>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="as-contact-tabs">
          <div className="as-tab-navigation">
            <button 
              className={`as-tab-btn ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              <span className="as-tab-label">All Contacts</span>
              <span className="as-tab-count">{cleaningResults?.total || 0}</span>
            </button>
            <button 
              className={`as-tab-btn ${activeTab === 'kept' ? 'active' : ''}`}
              onClick={() => setActiveTab('kept')}
            >
              <span className="as-tab-label">Kept</span>
              <span className="as-tab-count">{cleaningResults?.kept || 0}</span>
            </button>
            <button 
              className={`as-tab-btn ${activeTab === 'renamed' ? 'active' : ''}`}
              onClick={() => setActiveTab('renamed')}
            >
              <span className="as-tab-label">Renamed</span>
              <span className="as-tab-count">{cleaningResults?.renamed || 0}</span>
            </button>
            <button 
              className={`as-tab-btn ${activeTab === 'removed' ? 'active' : ''}`}
              onClick={() => setActiveTab('removed')}
            >
              <span className="as-tab-label">Removed</span>
              <span className="as-tab-count">{cleaningResults?.removed || 0}</span>
            </button>
          </div>

          {/* Tab Content */}
          <div className="as-tab-content">
            {renderContactList()}
          </div>
        </div>

        <div className="as-cleaning-actions">
          <div className="as-action-group">
            <h4 className="as-action-title">What would you like to do?</h4>
            
            <button 
              className="as-action-btn as-proceed-btn"
              onClick={async () => {
                // Entferne die gefilterten Kontakte dauerhaft aus der Datenbank
                if (removedContacts.length > 0) {
                  try {
                    const deleteBatch = writeBatch(firestore);
                    removedContacts.forEach(contact => {
                      const contactRef = doc(firestore, `World/${userId}/db/${contact.id}`);
                      deleteBatch.delete(contactRef);
                    });
                    await deleteBatch.commit();
                    console.log(`${removedContacts.length} removed contacts permanently deleted from Firestore`);
                  } catch (error) {
                    console.error("Error deleting removed contacts:", error);
                  }
                }
                
                // Fahre mit dem E-Mail-Versand fort
                handleProceedWithCleaned();
              }}
              disabled={cleanedContacts.length === 0}
            >
              <div className="as-btn-content">
                <div className="as-btn-text">
                  <span className="as-btn-title">Send Emails to Cleaned Contacts</span>
                  <span className="as-btn-subtitle">
                    Send to {cleanedContacts.length} cleaned contacts
                    {removedContacts.length > 0 && ` and permanently delete ${removedContacts.length} removed contacts`}
                  </span>
                </div>
              </div>
            </button>
          </div>
        </div>

        <div className="as-cleaning-cancel">
          <button 
            className="as-cancel" 
            onClick={handleClose}
          >
            Cancel Process
          </button>
        </div>
      </div>
    );
  };

  // Render Ergebnisbildschirm
  const renderResultScreen = () => {
    // Spezielle Behandlung für den Fall, dass alle Kontakte entfernt wurden
    if (results.allContactsRemoved) {
      return (
        <div className="as-result">
          <h2 className="as-result-title">
            <span className="as-warning">All Contacts Removed by Cleaning</span>
          </h2>

          <div className="as-autoresult-cleaning-summary">
            <h3 className="as-autoresult-title">Cleaning Results</h3>
            <div className="as-autoresult-stats-container">
              <div className="as-autoresult-stat-item as-autoresult-kept">
                <span className="as-autoresult-number">{cleaningResults?.kept || 0}</span>
                <span className="as-autoresult-label">Kept</span>
              </div>
              <div className="as-autoresult-stat-item as-autoresult-renamed">
                <span className="as-autoresult-number">{cleaningResults?.renamed || 0}</span>
                <span className="as-autoresult-label">Renamed</span>
              </div>
              <div className="as-autoresult-stat-item as-autoresult-removed">
                <span className="as-autoresult-number">{cleaningResults?.removed || 0}</span>
                <span className="as-autoresult-label">Removed</span>
              </div>
            </div>
          </div>

          <div className="as-no-contacts-message">
            <h3 className="as-no-contacts-title">No Contacts to Send To</h3>
            <p className="as-no-contacts-description">
              The AI Cleaner analyzed all {bulkContacts.length} selected contacts and determined that none of them meet your cleanup criteria. 
              All contacts have been removed from your database.
            </p>
            <div className="as-no-contacts-suggestions">
              <h4>What you can do:</h4>
              <ul>
                <li>Review your cleanup guidelines in the AI settings</li>
                <li>Try selecting different contacts</li>
                <li>Disable cleaning and send emails to the original contacts</li>
              </ul>
            </div>
          </div>

          <button 
            className="as-close" 
            onClick={handleClose}
          >
            Close
          </button>
        </div>
      );
    }

    // Normale Ergebnisanzeige
    return (
      <div className="as-result">
        <h2 className="as-result-title">
          {results.failed === 0 ? (
            <span className="as-success">All emails sent successfully!</span>
          ) : (
            <span className="as-partial">Process completed with some issues</span>
          )}
        </h2>

        {/* Email Results Summary - ganz oben */}
        <div className="as-autoresult-summary-grid">
          <div className="as-autoresult-summary-item as-autoresult-success">
            <span className="as-autoresult-summary-number">{results.success}</span>
            <span className="as-autoresult-summary-label">Sent</span>
          </div>
          <div className="as-autoresult-summary-item as-autoresult-failed">
            <span className="as-autoresult-summary-number">{results.failed}</span>
            <span className="as-autoresult-summary-label">Failed</span>
          </div>
          <div className="as-autoresult-summary-item as-autoresult-skipped">
            <span className="as-autoresult-summary-number">{results.skipped}</span>
            <span className="as-autoresult-summary-label">Skipped</span>
          </div>
        </div>

        {/* Liste der gesendeten E-Mails */}
        {sentEmails && sentEmails.length > 0 && (
          <div className="as-sent-emails-list">
            <h3 className="as-sent-emails-title">Sent Emails:</h3>
            <div className="as-sent-emails-container">
              <div className="as-sent-emails-grid">
                {sentEmails.map((email, index) => (
                  <div key={index} className={`as-email-card ${email && email.isScheduled ? 'as-scheduled-email' : ''}`}>
                    <div className="as-email-card-header">
                      <span className="as-email-name">{email ? (email.name || 'Unknown Contact') : 'Unknown Contact'}</span>
                      <span className="as-email-type">{email ? translateTopicName(email.topic || 'Unknown') : 'Unknown'}</span>
                    </div>
                    <div className="as-email-card-content">
                      <div className="as-email-address">
                        <span className="as-email-label">Email:</span>
                        <span className="as-email-value">{email ? (email.email || 'No email') : 'No email'}</span>
                      </div>
                      <div className="as-email-time">
                        <span className="as-email-label">Sending time:</span>
                        <span className="as-email-value">
                          {email && email.isScheduled 
                            ? <span className="as-scheduled-time">{formatScheduledDate(email.scheduledTime)}</span>
                            : 'Sent immediately'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Cleaning Results - aufklappbar */}
        {addCleaning && cleaningResults && (
          <div className="as-cleaning-results-container">
            <div 
              className="as-cleaning-results-header"
              onClick={() => setShowCleaningDetails(!showCleaningDetails)}
            >
              <h3 className="as-cleaning-results-title">
                Cleaning Results
                <span className="as-toggle-icon">
                  {showCleaningDetails ? '−' : '+'}
                </span>
              </h3>
              <div className="as-cleaning-results-summary">
                <div className="as-cleaning-stat">
                  <span className="as-stat-number">{cleaningResults.kept}</span>
                  <span className="as-cleaning-stat-label">Kept</span>
                </div>
                <div className="as-cleaning-stat">
                  <span className="as-stat-number">{cleaningResults.renamed}</span>
                  <span className="as-cleaning-stat-label">Renamed</span>
                </div>
                <div className="as-cleaning-stat">
                  <span className="as-stat-number">{cleaningResults.removed}</span>
                  <span className="as-cleaning-stat-label">Removed</span>
                </div>
              </div>
            </div>

            {showCleaningDetails && (
              <div className="as-cleaning-results-content">
                {/* Umbenannte Kontakte */}
                {cleaningResults.renamedContacts && cleaningResults.renamedContacts.length > 0 && (
                  <div className="as-cleaning-category">
                    <h4 className="as-cleaning-category-title">
                      Renamed Contacts ({cleaningResults.renamedContacts.length})
                    </h4>
                    <div className="as-cleaning-items-list">
                      {cleaningResults.renamedContacts.map((contact, index) => (
                        <div key={index} className="as-cleaning-item as-renamed-item">
                          <div className="as-cleaning-item-header">
                            <div className="as-name-change">
                              <span className="as-original-name">{contact.originalName}</span>
                              <span className="as-arrow">→</span>
                              <span className="as-cleaned-name">{contact.cleanedName}</span>
                            </div>
                            <span className="as-cleaning-item-email">{contact.email}</span>
                          </div>
                          <div className="as-cleaning-item-reason">
                            <span className="as-reason-label">Reason:</span>
                            <span className="as-reason-text">{contact.reason}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Entfernte Kontakte */}
                {cleaningResults.removedContacts && cleaningResults.removedContacts.length > 0 && (
                  <div className="as-cleaning-category">
                    <h4 className="as-cleaning-category-title">
                      Removed Contacts ({cleaningResults.removedContacts.length})
                    </h4>
                    <div className="as-cleaning-items-list">
                      {cleaningResults.removedContacts.map((contact, index) => (
                        <div key={index} className="as-cleaning-item as-removed-item">
                          <div className="as-cleaning-item-header">
                            <span className="as-cleaning-item-name">{contact.originalName}</span>
                            <span className="as-cleaning-item-email">{contact.email}</span>
                          </div>
                          <div className="as-cleaning-item-reason">
                            <span className="as-reason-label">Reason:</span>
                            <span className="as-reason-text">{contact.reason}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Auto Cleaning: Zeige entfernte Kontakte mit Aktionsoptionen */}
        {autoCleaningMode && removedContacts.length > 0 && (
          <div className="as-auto-cleaning-section">
            <h3 className="as-auto-cleaning-title">Removed Contacts ({removedContacts.length})</h3>
            <p className="as-auto-cleaning-description">
              These contacts were automatically removed during cleaning and are no longer in your database:
            </p>
            
            <div className="as-removed-contacts-preview">
              {removedContacts.slice(0, 3).map((contact, index) => (
                <div key={index} className="as-removed-contact-preview-item">
                  <span className="as-removed-contact-name">{contact.originalName}</span>
                  <span className="as-removed-contact-reason">{contact.reason}</span>
                </div>
              ))}
              {removedContacts.length > 3 && (
                <div className="as-more-removed-preview">
                  +{removedContacts.length - 3} more contacts removed
                </div>
              )}
            </div>

            <div className="as-auto-cleaning-actions">
              <button 
                className="as-action-btn as-restore-removed-btn"
                onClick={handleRestoreRemovedContacts}
              >
                <div className="as-btn-content">
                  <div className="as-btn-text">
                    <span className="as-btn-title">Restore Removed Contacts</span>
                    <span className="as-btn-subtitle">Add {removedContacts.length} contacts back to your database</span>
                  </div>
                </div>
              </button>

              <button 
                className="as-action-btn as-keep-removed-btn"
                onClick={handleKeepContactsDeleted}
              >
                <div className="as-btn-content">
                  <div className="as-btn-text">
                    <span className="as-btn-title">Keep Contacts Deleted</span>
                    <span className="as-btn-subtitle">Permanently delete {removedContacts.length} removed contacts</span>
                  </div>
                </div>
              </button>
            </div>
          </div>
        )}

        {results.errors && results.errors.length > 0 && (
          <div className="as-error-list">
            <h3 className="as-error-title">Errors for these contacts:</h3>
            <div className="as-error-container">
              {results.errors.filter(error => error && typeof error === 'object').map((error, index) => (
                <div key={index} className="as-error-item">
                  <div className="as-error-header">
                    <span className="as-error-name">{error.name || 'Unknown Contact'}</span>
                    <span className="as-error-id">ID: {error.id && typeof error.id === 'string' ? error.id.substring(0, 6) : 'unknown'}...</span>
                  </div>
                  <div className="as-error-message">{error.error || 'Unknown error'}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <button 
          className="as-close" 
          onClick={handleClose}
          disabled={isProcessing}
        >
          Close
        </button>
      </div>
    );
  };

  // Füge einen sicheren onClose-Handler hinzu
  const handleClose = () => {
    // Verhindere das Schließen während der Verarbeitung
    if (isProcessing) {
      console.log("Schließen während der Verarbeitung verhindert");
      return;
    }
    
    // Normaler onClose-Aufruf
    onClose();
  };

  return (
    <div 
      className="as-overlay"
      style={isProcessing ? { pointerEvents: isConfirmed ? 'all' : 'auto' } : {}}
    >
      <div className="as-modal">
        <div className="as-header">
          <h2 className="as-title">AutoSend Email Manager</h2>
          <button 
            className="as-close-btn" 
            onClick={handleClose}
            disabled={isProcessing}
            style={isProcessing ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            {isProcessing ? 
              <span title="Cannot close during processing">×</span> : 
              '×'
            }
          </button>
        </div>
        
        <div className="as-content">
          {currentStep === 'confirmation' && renderConfirmationScreen()}
          {(currentStep === 'cleaning' || currentStep === 'sending') && renderProgressScreen()}
          {currentStep === 'cleaningResults' && renderCleaningResultsScreen()}
          {currentStep === 'complete' && renderResultScreen()}
        </div>
      </div>
    </div>
  );
};

export default AutoSendModeEmail; 